import apiClient from './api';
import {
  EquipmentData,
  EquipmentDataDTO,
  EquipmentUpdateData,
  ConsumableData,
  ConsumableDataDTO,
  ConsumableUpdateData,
  CollaboratorData,
  CollaboratorDataDTO,
  CollaboratorUpdateData,
  ServiceData,
  ServiceDataDTO,
  ResourceType,
  ResourcePricingUpdate,
} from '../models/Resource';

const generateUniqueHandle = (name: string): string =>
  name.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();

const toUnitPrice = (value: string | number): number =>
  Number(String(value).replace(',', '.'));

const syncResourcePricing = async (
  resourceId: string,
  pricing: ResourcePricingUpdate
): Promise<void> => {
  const unitPrice = toUnitPrice(pricing.unit_price);

  if (!pricing.pricing_id) {
    await apiClient.post(`/service-providers/execution-resources/${resourceId}/pricing`, {
      unit: pricing.unit,
      unit_price: unitPrice,
    });
    return;
  }

  if (pricing.current_unit && pricing.current_unit !== pricing.unit) {
    await apiClient.delete(
      `/service-providers/execution-resources/${resourceId}/pricing/${pricing.pricing_id}`
    );
    await apiClient.post(`/service-providers/execution-resources/${resourceId}/pricing`, {
      unit: pricing.unit,
      unit_price: unitPrice,
    });
    return;
  }

  await apiClient.patch(
    `/service-providers/execution-resources/${resourceId}/pricing/${pricing.pricing_id}`,
    { unit_price: unitPrice }
  );
};

export async function getAllExecutionResources() {
  const response = await apiClient.get('/service-providers/execution-resources', {
    params: { limit: 200 },
  });
  return response.data;
}

export async function createEquipment(equipmentData: EquipmentData, type: ResourceType) {
  const dto: EquipmentDataDTO = {
    name: equipmentData.name,
    description: equipmentData.description,
    type,
    pricing_options: [
      { unit: equipmentData.unit, unit_price: toUnitPrice(equipmentData.unit_price) },
    ],
    metadata: {
      brand: equipmentData.brand,
      model: equipmentData.model,
      serial_number: equipmentData.serial_number,
      power: equipmentData.power,
      purchase_date: equipmentData.purchase_date,
      acquisition_value: equipmentData.acquisition_value,
    },
  };
  const response = await apiClient.post('/service-providers/execution-resources', dto);
  return response.data;
}

export async function updateEquipment(id: string, payload: EquipmentUpdateData) {
  const response = await apiClient.patch(`/service-providers/execution-resources/${id}`, {
    name: payload.name,
    description: payload.description,
    metadata: payload.metadata,
  });
  await syncResourcePricing(id, payload.pricing);
  return response.data;
}

export async function getAllEquipments() {
  return getAllExecutionResources();
}

export async function deleteEquipment(id: string) {
  await apiClient.delete(`/service-providers/execution-resources/${id}`);
}

export async function createConsumable(consumableData: ConsumableData) {
  const dto: ConsumableDataDTO = {
    name: consumableData.name,
    description: consumableData.description,
    type: ResourceType.CONSUMABLE,
    pricing_options: [
      { unit: consumableData.unit, unit_price: toUnitPrice(consumableData.unit_price) },
    ],
    metadata: {
      brand: consumableData.brand,
      reference: consumableData.reference,
      purchase_date: consumableData.purchase_date,
      acquisition_value: consumableData.acquisition_value,
    },
  };
  const response = await apiClient.post('/service-providers/execution-resources', dto);
  return response.data;
}

export async function updateConsumable(id: string, payload: ConsumableUpdateData) {
  const response = await apiClient.patch(`/service-providers/execution-resources/${id}`, {
    name: payload.name,
    description: payload.description,
    metadata: payload.metadata,
  });
  await syncResourcePricing(id, payload.pricing);
  return response.data;
}

export async function getAllConsumables() {
  return getAllExecutionResources();
}

export async function deleteConsumable(id: string) {
  await apiClient.delete(`/service-providers/execution-resources/${id}`);
}

export async function createCollaborator(collaboratorData: CollaboratorData) {
  const dto: CollaboratorDataDTO = {
    name: collaboratorData.name,
    description: collaboratorData.description,
    type: ResourceType.MAN_POWER,
    pricing_options: [
      { unit: collaboratorData.unit, unit_price: toUnitPrice(collaboratorData.unit_price) },
    ],
    metadata: {
      job: collaboratorData.job,
      birth_date: collaboratorData.birth_date,
    },
  };
  const response = await apiClient.post('/service-providers/execution-resources', dto);
  return response.data;
}

export async function updateCollaborator(id: string, payload: CollaboratorUpdateData) {
  const response = await apiClient.patch(`/service-providers/execution-resources/${id}`, {
    name: payload.name,
    description: payload.description,
    metadata: payload.metadata,
  });
  await syncResourcePricing(id, payload.pricing);
  return response.data;
}

export async function getAllCollaborators() {
  return getAllExecutionResources();
}

export async function deleteCollaborator(id: string) {
  await apiClient.delete(`/service-providers/execution-resources/${id}`);
}

export async function createService(serviceData: ServiceData) {
  const dto: ServiceDataDTO = {
    title: serviceData.name,
    status: 'published',
    description: serviceData.description,
    handle: generateUniqueHandle(serviceData.name),
    options: [{ title: 'Default', values: ['default'] }],
    categories: serviceData.category
      ? [
          { id: serviceData.category },
          ...(serviceData.sub_category ? [{ id: serviceData.sub_category }] : []),
        ]
      : [],
    variants: [
      {
        title: 'Default-Variant',
        prices: [{ currency_code: 'eur', amount: toUnitPrice(serviceData.unit_price) }],
        manage_inventory: false,
        options: { Default: 'default' },
        metadata: { unit: serviceData.unit, tax_value: serviceData.tax_value },
      },
    ],
  };
  const response = await apiClient.post('/service-providers/products', dto);
  return response.data;
}

export async function getAllServices() {
  const response = await apiClient.get('/service-providers/products', {
    params: { limit: 200 },
  });
  return response.data;
}

export async function deleteService(id: string) {
  await apiClient.delete(`/service-providers/products/${id}`);
}

export async function updateService(id: string, serviceData: ServiceData) {
  const payload = {
    title: serviceData.name,
    description: serviceData.description,
    variants: [
      {
        id: serviceData.id_variant,
        prices: [{ id: serviceData.id_price, amount: toUnitPrice(serviceData.unit_price) }],
        metadata: { unit: serviceData.unit, tax_value: serviceData.tax_value },
      },
    ],
  };
  const response = await apiClient.post(`/service-providers/products/${id}`, payload);
  return response.data;
}

export async function getQuotes() {
  const response = await apiClient.get('/service-providers/quotes');
  return response.data;
}

export async function getQuote(id: string) {
  const response = await apiClient.get(`/service-providers/quotes/${id}`);
  return response.data;
}

export async function sendQuote(id: string) {
  const response = await apiClient.post(`/service-providers/quotes/${id}/send`);
  return response.data;
}

export async function rejectQuote(id: string) {
  const response = await apiClient.post(`/service-providers/quotes/${id}/reject`);
  return response.data;
}

export async function createQuoteMessage(id: string, text: string) {
  const response = await apiClient.post(`/service-providers/quotes/${id}/messages`, { text });
  return response.data;
}
